import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import getWorkersKyosk from "../../actions/workerUserActions/fetchWorkersKyosk";
import { useSelector } from "react-redux";
import { setKyoskWorker, resetKyosk, getWorkersNextPunchType, punchCoWorker } from "../../features/foremanActions/kyoskSlice";
import FacialLiveness from "../FacialLiveness";
import { useDispatch } from "react-redux";

// The prop jobsiteId is the id of the jobbsite card that the foreman is currently VIEWING
const KyoskModal = ({ onClose, jobsiteId: selectedJobsiteCard }) => {
  
  
  const dispatch = useDispatch();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);



  const {workerUser, workerLocation} = useSelector((state) => state.workerAuth);
  const {selectedWorker, nextPunchType, message : kyoskMessage} = useSelector((state) => state.kyosk);
  const {jobsite: currentJobsite, isError, message, isSuccess} = useSelector((state) => state.currentJobsite);
  // Toast kyoskMessage when it changes
  useEffect(() => {
    if (kyoskMessage) {
      toast(kyoskMessage);
    }
  }, [kyoskMessage]);

  // State for liveness check
  const [showLiveness, setShowLiveness] = useState(false);

  
  const debounceRef = useRef();

  // Fetch List of enrolled workers with debounce for search, only if
  //selectedJobsiteCard matches the currentJobsite
  useEffect(() => {

    if (!currentJobsite || selectedJobsiteCard !== currentJobsite.id) {
      setWorkers([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const fetchWorkers = async () => {
        try {
          const data = await getWorkersKyosk(workerUser.webToken, selectedJobsiteCard, search, page);
          setWorkers(data.workers || []);
          setHasMore(data.hasMore || false);
        } catch (err) {
          setError("Failed to fetch workers");
        } finally {
          setLoading(false);
        }
      };
      fetchWorkers();
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [workerUser.webToken, selectedJobsiteCard, search, page, currentJobsite]);

 // When a selected worker changes, dispatch getWorkersNextPunchType
useEffect(() => {
 
  if (selectedWorker) {
    dispatch(getWorkersNextPunchType({
      coWorkerId: selectedWorker,
      jobsiteId: selectedJobsiteCard
    }));
  }
}, [selectedWorker, dispatch, selectedJobsiteCard]);




  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };
  const handleNextPage = () => {
    if (hasMore) setPage(page + 1);
  };



  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        {/* Close X button */}
        <button
          className="absolute top-3 right-3 text-orangem text-2xl font-bold focus:outline-none"
          onClick={() => {
            dispatch(resetKyosk());
            onClose();
          }}
          aria-label="Close"
        >
          ×
        </button>
        <h3 className="text-lg font-bold mb-1 text-teal">
          {workerUser.language === "spa" ? "Kyosko" : "Kyosk"}
        </h3>
        <p className="text-sm italic mb-2">
          {workerUser.language === "spa"
            ? "Seleccione un trabajador de la lista para registrar entrada/salida."
            : "Select worker from list below to punch in/out."}
        </p>
        {currentJobsite && selectedJobsiteCard === currentJobsite.id && (
          <input
            type="text"
            className="w-full mb-4 px-3 py-2 rounded-full border border-robin focus:outline-none focus:ring-2 focus:ring-teal text-charcoal bg-sage"
            placeholder={workerUser.language === "spa" ? "Nombre o apellido..." : "Name or last name..."}
            value={search}
            onChange={handleSearchChange}
          />
        )}
        {loading ? (
          <p className="text-teal">Loading workers...</p>
        ) : (
          error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="overflow-y-auto max-h-64">
              {(!currentJobsite || selectedJobsiteCard !== currentJobsite.id) ? (
                <p className="text-charcoal">You must be on site to access this feature</p>
              ) : workers.length === 0 ? (
                <p className="text-charcoal">No workers found.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {workers.map((worker) => (
                    <li key={worker.id}>
                      <button
                        className="flex items-center w-full hover:bg-teal hover:text-sage text-charcoal font-semibold rounded-full px-4 py-2 transition-all"
                        onClick={() => dispatch(setKyoskWorker(worker.id))}
                      >
                        <img
                          src={worker.profilePicture || "/workerSilhoutte.png"}
                          alt={worker.name}
                          className="w-20 h-20 rounded-full mr-3 border-2 border-teal object-cover"
                        />
                        <span className="truncate text-left">{worker.name} {worker.lastName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        )}

        {currentJobsite && selectedJobsiteCard === currentJobsite.id && (
          <div className="flex justify-between items-center mt-4">
            <button
              className="px-4 py-2 bg-teal text-white rounded disabled:opacity-50"
              onClick={handlePrevPage}
              disabled={page === 1}
            >
              {workerUser.language === "spa" ? "Anterior" : "Prev"}
            </button>
            {nextPunchType && !showLiveness && (
              nextPunchType === "N/A" ? (
                <span className="text-charcoal font-semibold">
                  {workerUser.language === "spa"
                    ? "Entrada y Salida registrada"
                    : "Already clocked in and out"}
                </span>
              ) : (
                <button
                  className={`px-4 py-2 font-bold rounded ${nextPunchType === "clockIn" ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}
                  onClick={() => {
                    setShowLiveness(true);
                  }}
                >
                  {nextPunchType === "clockIn"
                    ? (workerUser.language === "spa" ? "Registrar Entrada" : "Clock In")
                    : (workerUser.language === "spa" ? "Registrar Salida" : "Clock Out")}
                </button>
              )
            )}
        {/* Facial Liveness Modal */}
        {showLiveness && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-70">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
              <FacialLiveness onLivenessResult={async (isLive, sessionId) => {
                  setShowLiveness(false);
                  // Log the result and context
                  const punchData = {
                    selectedWorkerId: selectedWorker,
                    sessionId : sessionId,
                    jobsiteId: selectedJobsiteCard,
                    punchType: nextPunchType,
                    
                  }
                  if( isLive === true){
                    dispatch(punchCoWorker(punchData)) // why is await unnecessary?
                  }
                
                }}
              />
            </div>
          </div>
        )}
            <button
              className="px-4 py-2 bg-teal text-white rounded disabled:opacity-50"
              onClick={handleNextPage}
              disabled={!hasMore}
            >
              {workerUser.language === "spa" ? "Siguiente" : "Next"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KyoskModal;


//need to implement, when a worker is selected, have a button appear that
//either says clock in or clock out, depending on the next punch type for that worker at the jobsite.

//when closing the modal, reset the kyosk state